import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import { useEffect, useRef, useCallback, useState, useContext } from 'react'
import { Download, Pencil, Bold, Italic, Underline as UnderlineIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { ChainlitContext } from '@chainlit/react-client'

function preprocessHtml(raw: string): string {
  if (!raw) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(raw, 'text/html')
  doc.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => el.remove())
  Array.from(doc.querySelectorAll('.field-filled')).forEach(span => {
    span.replaceWith(doc.createTextNode(span.textContent || ''))
  })
  Array.from(doc.querySelectorAll('.sig-block')).forEach(sigBlock => {
    let sigCols = Array.from(sigBlock.querySelectorAll(':scope > .sig-col'))
    if (!sigCols.length) sigCols = Array.from(sigBlock.children)
    if (!sigCols.length) { sigBlock.remove(); return }
    const table = doc.createElement('table')
    table.setAttribute('style', 'width:100%;margin-top:40px;border-collapse:collapse')
    const tr = doc.createElement('tr')
    sigCols.forEach(col => {
      const line = col.querySelector('.sig-line') || col
      const td = doc.createElement('td')
      td.setAttribute('style', 'border-top:1px solid #333;width:48%;padding-top:8px;padding-right:20px;vertical-align:top;border-left:none;border-right:none;border-bottom:none')
      td.innerHTML = line.innerHTML
      tr.appendChild(td)
    })
    table.appendChild(tr)
    sigBlock.parentNode?.replaceChild(table, sigBlock)
  })
  Array.from(doc.querySelectorAll('div.fr')).forEach(fr => {
    const fl = fr.querySelector(':scope > .fl')
    const fv = fr.querySelector(':scope > .fv')
    if (!fl || !fv) return
    const p = doc.createElement('p')
    const strong = doc.createElement('strong')
    strong.textContent = fl.textContent?.trim() || ''
    p.appendChild(strong)
    p.appendChild(doc.createTextNode(': '))
    Array.from(fv.childNodes).forEach(n => p.appendChild(n.cloneNode(true)))
    fr.parentNode?.replaceChild(p, fr)
  })
  Array.from(doc.querySelectorAll('.annex-box')).forEach(box => {
    const bq = doc.createElement('blockquote')
    bq.setAttribute('data-annex', 'true')
    const label = box.querySelector('.annex-label')
    const value = box.querySelector('.annex-value')
    if (label) { const lp = doc.createElement('p'); const s = doc.createElement('strong'); s.textContent = label.textContent?.trim() || ''; lp.appendChild(s); bq.appendChild(lp) }
    if (value) { const vp = doc.createElement('p'); vp.textContent = value.textContent?.trim() || ''; bq.appendChild(vp) }
    box.parentNode?.replaceChild(bq, box)
  })
  Array.from(doc.querySelectorAll('.page-break')).forEach(pb => pb.replaceWith(doc.createElement('hr')))
  Array.from(doc.querySelectorAll('.in2')).forEach(div => {
    const outer = doc.createElement('blockquote'); const inner = doc.createElement('blockquote'); const p = doc.createElement('p')
    p.innerHTML = div.innerHTML; inner.appendChild(p); outer.appendChild(inner)
    div.replaceWith(outer)
  })
  Array.from(doc.querySelectorAll('.in1')).forEach(div => {
    const bq = doc.createElement('blockquote'); const p = doc.createElement('p')
    p.innerHTML = div.innerHTML; bq.appendChild(p)
    div.replaceWith(bq)
  })

  // .doc-title → h1
  Array.from(doc.querySelectorAll('h1.doc-title, .doc-title')).forEach(el => {
    const h1 = doc.createElement('h1')
    h1.innerHTML = el.innerHTML
    el.parentNode?.replaceChild(h1, el)
  })

  // .doc-subtitle → italic paragraph
  Array.from(doc.querySelectorAll('.doc-subtitle')).forEach(el => {
    const p = doc.createElement('p')
    const em = doc.createElement('em')
    em.textContent = el.textContent?.trim() || ''
    p.appendChild(em)
    el.parentNode?.replaceChild(p, el)
  })

  // .section-title / .doc-section → h2
  Array.from(doc.querySelectorAll('.section-title, .doc-section')).forEach(el => {
    const h2 = doc.createElement('h2')
    h2.textContent = el.textContent?.trim() || ''
    el.replaceWith(h2)
  })

  // .clause-title → h4
  Array.from(doc.querySelectorAll('.clause-title')).forEach(el => {
    const h4 = doc.createElement('h4')
    h4.textContent = el.textContent?.trim() || ''
    el.parentNode?.replaceChild(h4, el)
  })

  // .info-box → blockquote (obligation lists, numbered clauses)
  Array.from(doc.querySelectorAll('.info-box')).forEach(box => {
    const bq = doc.createElement('blockquote')
    bq.setAttribute('data-info', 'true')
    bq.innerHTML = box.innerHTML
    box.parentNode?.replaceChild(bq, box)
  })

  // .bilaga-table → keep as table, already handled by TipTap
  // .clause-text divs → unwrap to their inner content
  Array.from(doc.querySelectorAll('.clause-text')).forEach(el => {
    const p = doc.createElement('p')
    p.innerHTML = el.innerHTML
    el.parentNode?.replaceChild(p, el)
  })

  // .row/.col party field blocks → label: value paragraphs
  Array.from(doc.querySelectorAll('.row')).forEach(row => {
    const cols = Array.from(row.querySelectorAll('.col'))
    if (!cols.length) return
    const frag = doc.createDocumentFragment()
    cols.forEach(col => {
      const fl = col.querySelector('.fl')
      const fv = col.querySelector('.fv')
      if (!fl && !fv) return
      const p = doc.createElement('p')
      if (fl) {
        const strong = doc.createElement('strong')
        strong.textContent = fl.textContent?.trim() || ''
        p.appendChild(strong)
        p.appendChild(doc.createTextNode(': '))
      }
      if (fv) p.appendChild(doc.createTextNode(fv.textContent?.trim() || ''))
      frag.appendChild(p)
    })
    row.parentNode?.replaceChild(frag, row)
  })

  // unwrap bare .section wrappers
  Array.from(doc.querySelectorAll('.section')).forEach(el => {
    const frag = doc.createDocumentFragment()
    Array.from(el.childNodes).forEach(n => frag.appendChild(n.cloneNode(true)))
    el.parentNode?.replaceChild(frag, el)
  })

  const wrap = doc.querySelector('.doc-wrap')
  return wrap ? wrap.innerHTML : doc.body.innerHTML
}

const TEMPLATE_FILENAMES: Record<string, string> = {
  anstallningsavtal: 'anstallningsavtal.docx',
  nda: 'sekretessavtal.docx',
  konsultavtal: 'konsultavtal.docx',
  personuppgiftsbitradeavtal: 'personuppgiftsbitradeavtal.docx',
  aktieagaravtal: 'aktieagaravtal.docx',
}

interface Props {
  document: string
  phase: string
  contractId: string
  templateId: string
  apiFetch: (path: string, options?: RequestInit) => Promise<Response>
  activeTab?: 'chat' | 'preview'
  onTabChange?: (tab: 'chat' | 'preview') => void
}

export default function DocumentPreview({ document: docHtml, phase, contractId, templateId, apiFetch, activeTab, onTabChange }: Props) {
  const apiClient = useContext(ChainlitContext)
  const [editable, setEditable] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const prevHtmlRef = useRef('')

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ blockquote: { HTMLAttributes: {} } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: false }),
      TableRow, TableHeader, TableCell,
    ],
    content: '',
    editable: false,
    editorProps: { attributes: { class: 'doc-canvas' } },
  })

  useEffect(() => { if (!editor) return; editor.setEditable(editable) }, [editor, editable])

  useEffect(() => {
    if (!editor || !docHtml) return
    if (docHtml === prevHtmlRef.current) return
    prevHtmlRef.current = docHtml
    editor.commands.setContent(preprocessHtml(docHtml), false)
  }, [editor, docHtml])

  const handleDownload = useCallback(async () => {
    if (!editor || downloading) return
    setDownloading(true)
    try {
      const html = editor.getHTML()
      const res = await apiFetch(`/api/contracts/${contractId}/document/export`, {
        method: 'POST',
        body: JSON.stringify({ html }),
      })
      if (!res.ok) { toast.error('Nedladdningen misslyckades.'); return }
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = Object.assign(document.createElement('a'), {
        href: url,
        download: TEMPLATE_FILENAMES[templateId] || `${templateId}.docx`,
      })
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(false)
    }
  }, [editor, contractId, templateId, apiFetch, downloading])

  const isEmpty = !docHtml

  return (
    <div className="flex flex-col h-full border-l bg-background overflow-hidden">
      {/* Top bar */}
      <div className="h-11 flex items-center gap-2 px-4 border-b bg-background shrink-0">
        {/* Mobile tab switcher */}
        {onTabChange && (
          <div className="flex md:hidden items-center gap-1 mr-2">
            <button
              onClick={() => onTabChange('chat')}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-colors',
                activeTab === 'chat'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Chatt
            </button>
            <button
              onClick={() => onTabChange('preview')}
              className={cn(
                'px-3 py-1 rounded text-xs font-medium transition-colors',
                activeTab === 'preview'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Förhandsgranskning
            </button>
          </div>
        )}
        <span className="hidden md:inline text-xs text-muted-foreground tracking-wide">Förhandsgranskning</span>

        <div className="flex items-center gap-1 ml-auto">
          {!isEmpty && (
            <>
              {/* Edit button — desktop only */}
              <Button
                variant={editable ? 'secondary' : 'ghost'}
                size="icon"
                className="hidden md:flex h-7 w-7"
                onClick={() => setEditable(v => !v)}
                title="Redigera"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>

              {editable && editor && (
                <>
                  <Separator orientation="vertical" className="hidden md:block h-5" />
                  <Button
                    variant={editor.isActive('bold') ? 'secondary' : 'ghost'}
                    size="icon"
                    className="hidden md:flex h-7 w-7"
                    onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBold().run() }}
                  >
                    <Bold className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant={editor.isActive('italic') ? 'secondary' : 'ghost'}
                    size="icon"
                    className="hidden md:flex h-7 w-7"
                    onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleItalic().run() }}
                  >
                    <Italic className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant={editor.isActive('underline') ? 'secondary' : 'ghost'}
                    size="icon"
                    className="hidden md:flex h-7 w-7"
                    onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleUnderline().run() }}
                  >
                    <UnderlineIcon className="h-3.5 w-3.5" />
                  </Button>
                  <Separator orientation="vertical" className="hidden md:block h-5" />
                </>
              )}

              {/* Download — icon only on mobile, text on desktop */}
              <Button
                variant="default"
                size="icon"
                className="h-7 w-7 md:hidden"
                onClick={handleDownload}
                disabled={downloading}
                title="Ladda ner"
              >
                {downloading ? <span className="text-[10px]">…</span> : <Download className="h-3.5 w-3.5" />}
              </Button>
              <Button
                variant="default"
                size="sm"
                className="hidden md:flex h-7 text-xs"
                onClick={handleDownload}
                disabled={downloading}
              >
                <Download className="h-3 w-3 mr-1" />
                {downloading ? '…' : 'Ladda ner'}
              </Button>
            </>
          )}

          <Badge
            variant={phase === 'complete' ? 'default' : 'outline'}
            className={cn(
              'text-[10px] font-semibold ml-1 shrink-0',
              phase === 'complete' && 'bg-green-600 hover:bg-green-600',
              !isEmpty && phase !== 'complete' && 'border-primary text-primary',
            )}
          >
            {phase === 'complete' ? 'KLAR' : isEmpty ? 'VÄNTAR' : 'LIVE'}
          </Badge>
        </div>
      </div>

      {/* Document area */}
      <div className="flex-1 min-h-0" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="p-3 md:p-5">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center gap-4 text-muted-foreground py-20 text-center">
              <div className="w-16 h-16 border-2 border-dashed border-border rounded-2xl flex items-center justify-center p-2">
                <img
                  src={apiClient?.buildEndpoint('/avatars/default')}
                  alt="Assistant"
                  className="w-full h-full object-contain rounded-xl"
                />
              </div>
              <p className="text-sm leading-relaxed">
                Ditt avtal visas här medan<br />agenten samlar in information.
              </p>
            </div>
          ) : (
            <div className="bg-white mx-auto shadow-lg rounded-sm px-4 py-6 md:px-14 md:py-12" style={{ maxWidth: '740px', width: '100%', boxSizing: 'border-box' }}>
              <EditorContent editor={editor} />
            </div>
          )}
        </div>
      </div>

      <style>{EDITOR_CSS}</style>
    </div>
  )
}

const EDITOR_CSS = `
.doc-canvas { outline:none; font-family:Georgia,'Times New Roman',serif; font-size:11pt; color:#1a1a1a; line-height:1.6; max-width:100%; overflow:hidden; word-break:break-word; overflow-wrap:break-word; }
.doc-canvas .ProseMirror { max-width:100%; overflow:hidden; }
.doc-canvas h1 { font-family:Arial,Helvetica,sans-serif; text-align:center; font-size:17pt; font-weight:800; letter-spacing:.04em; text-transform:uppercase; margin:0 0 4px; color:#111; }
.doc-canvas h1 + p em { display:block; text-align:center; font-size:9.5pt; color:#666; margin-bottom:22px; font-style:italic; }
.doc-canvas h2 { font-family:Arial,Helvetica,sans-serif; font-size:9.5pt; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#333; border-bottom:1.5px solid #555; padding-bottom:3px; margin:22px 0 8px; }
.doc-canvas h3 { font-family:Arial,Helvetica,sans-serif; font-size:10pt; font-weight:700; text-transform:uppercase; letter-spacing:.04em; border-bottom:1px solid #aaa; padding-bottom:2px; margin:18px 0 6px; }
.doc-canvas h4 { font-family:Arial,Helvetica,sans-serif; font-size:10.5pt; font-weight:700; margin:12px 0 4px; color:#222; }
.doc-canvas p { margin:5px 0; font-size:11pt; line-height:1.6; }
.doc-canvas table { width:100%; border-collapse:collapse; margin:10px 0 14px; table-layout:fixed; }
.doc-canvas td,.doc-canvas th { border:1px solid #aaa; padding:6px 10px; vertical-align:top; font-size:10.5pt; word-break:break-word; overflow-wrap:break-word; }
.doc-canvas th { background:#f3f4f6; font-weight:700; font-family:Arial,Helvetica,sans-serif; font-size:9.5pt; color:#444; }
.doc-canvas td p,.doc-canvas th p { margin:1px 0; }
.doc-canvas blockquote { margin:6px 0 6px 0; padding:8px 14px; border-left:none; background:#f8f9fa; border:1px solid #e2e4e8; border-radius:3px; }
.doc-canvas blockquote p { margin:3px 0; font-size:10.5pt; }
.doc-canvas blockquote blockquote { margin-left:18px; }
.doc-canvas blockquote[data-annex] { border:1px solid #aaa; border-radius:3px; padding:8px 12px; background:#fafafa; margin:8px 0; }
.doc-canvas hr { border:none; border-top:2px dashed #ccc; margin:24px 0 18px; }
.doc-canvas ul { list-style:disc; padding-left:22px; margin:6px 0; }
.doc-canvas ol { list-style:decimal; padding-left:22px; margin:6px 0; }
.doc-canvas li { margin:3px 0; font-size:11pt; line-height:1.55; }
.doc-canvas .field-filled { background:none!important; color:inherit!important; }
`
